/**
 * Utility functions for Control Graph operations
 */

import { Node, Edge } from "reactflow";
import dagre from "dagre";
import {
  ControlGraph,
  ControlNode,
  NodeType,
  NODE_METADATA,
  TransformKind,
  VariableReferenceType,
} from "./types";

/**
 * Infer node type from node properties when node_tag is missing.
 *
 * This is a fallback for backward compatibility with older serialized control graphs
 * that don't include node_tag. Modern backend properly serializes node_tag via the
 * msgspec_to_pydantic converter, but we keep this fallback for robustness.
 *
 * We detect the type by checking for unique required fields of each node type.
 */
function inferNodeType(node: any): string {
  // Check for unique properties that identify each node type
  if (node.variables !== undefined) return "declare";
  if (node.goal_question !== undefined || node.question_id !== undefined) return "question";
  if (node.criteria !== undefined && !Array.isArray(node.criteria)) return "analyze";
  if (node.template !== undefined) return "share_info";
  if (node.source_set_var !== undefined && node.iter_var !== undefined) return "map";
  if (node.expr !== undefined) return "if";
  if (node.guidance !== undefined &&
      (node.valence_delta !== undefined ||
       node.activation_delta !== undefined ||
       node.engagement_delta !== undefined)) return "influence";
  if (node.target !== undefined && node.source !== undefined) {
    // Both assign and append have target/source, differentiate by node_tag if present
    // or default to assign_action (most common)
    return "assign_action";
  }

  // Default fallback: if no specific properties, it's likely a sequence or group
  // We can't reliably distinguish between sequence and group without node_tag,
  // so default to sequence as it's more common
  return "sequence";
}

/**
 * Convert backend ControlGraph to React Flow nodes and edges
 */
export function controlGraphToReactFlow(
  controlGraph: ControlGraph | null
): { nodes: Node[]; edges: Edge[] } {
  if (!controlGraph || !controlGraph.nodes) {
    return { nodes: [], edges: [] };
  }

  const nodes: Node[] = [];
  const edges: Edge[] = [];

  // Convert each node
  Object.values(controlGraph.nodes).forEach((node, index) => {
    // If node_tag is missing, infer it from node properties
    const nodeTag = node.node_tag || inferNodeType(node);
    const metadata = NODE_METADATA[nodeTag as NodeType];

    nodes.push({
      id: node.id,
      type: "customNode",
      position: { x: 100 + (index % 4) * 300, y: 100 + Math.floor(index / 4) * 150 },
      data: {
        ...node,
        node_tag: nodeTag, // Ensure node_tag is always present in data
        label: metadata?.label || nodeTag,
        color: metadata?.color || "#6B7280",
        icon: metadata?.icon || "📌",
      },
    });

    // Create edges from parent-child relationships
    node.children.forEach((childId, childIndex) => {
      // For sequence nodes, add order information to the edge
      const edgeData = nodeTag === "sequence" ? { order: childIndex + 1 } : {};

      edges.push({
        id: `${node.id}-${childId}`,
        source: node.id,
        target: childId,
        type: nodeTag === "sequence" ? "sequence" : "smoothstep",
        animated: false,
        data: edgeData,
      });
    });
  });

  return { nodes, edges };
}

/**
 * Convert React Flow nodes and edges back to backend ControlGraph
 */
export function reactFlowToControlGraph(
  nodes: Node[],
  edges: Edge[],
  rootId: string
): ControlGraph {
  const controlNodes: Record<string, ControlNode> = {};

  // Build children map from edges
  const childrenMap: Record<string, string[]> = {};
  const parentMap: Record<string, string> = {};

  edges.forEach((edge) => {
    if (!childrenMap[edge.source]) {
      childrenMap[edge.source] = [];
    }
    childrenMap[edge.source].push(edge.target);
    parentMap[edge.target] = edge.source;
  });

  // Convert each node
  nodes.forEach((node) => {
    const nodeData = node.data;
    const children = childrenMap[node.id] || [];
    const parent = parentMap[node.id] || null;

    controlNodes[node.id] = {
      ...nodeData,
      id: node.id,
      children,
      parent,
    } as ControlNode;
  });

  return {
    nodes: controlNodes,
    root: rootId,
  };
}

/**
 * Create a new node with default values
 */
export function createDefaultNode(
  type: NodeType,
  id: string,
  position?: { x: number; y: number }
): Partial<ControlNode> {
  const base = {
    id,
    parent: null,
    children: [],
    priority: 0,
    is_exclusive_open: NODE_METADATA[type].isExclusive,
  };

  switch (type) {
    case NodeType.DECLARE:
      return {
        ...base,
        node_tag: "declare",
        variables: [],
      } as any;

    case NodeType.SEQUENCE:
      return {
        ...base,
        node_tag: "sequence",
      } as any;

    case NodeType.GROUP:
      return {
        ...base,
        node_tag: "group",
      } as any;

    case NodeType.MAP:
      return {
        ...base,
        node_tag: "map",
        source_set_var: "",
        iter_var: "",
      } as any;

    case NodeType.IF:
      return {
        ...base,
        node_tag: "if",
        expr: "",
      } as any;

    case NodeType.ANALYZE:
      return {
        ...base,
        node_tag: "analyze",
        criteria: {
          id: `criteria_${Date.now()}`,
          analyze_question: "",
          guidance: null,
          complete_required: false,
          answer_type: "string",
          answer_guidance: null,
          consider_asked_when_primary_is_asked: false,
        },
        close_on_finalize: true,
      } as any;

    case NodeType.SHARE_INFO:
      return {
        ...base,
        node_tag: "share_info",
        template: "",
        is_exclusive_open: true,
      } as any;

    case NodeType.INFLUENCE:
      return {
        ...base,
        node_tag: "influence",
        guidance: null,
        valence_delta: null,
        activation_delta: null,
        engagement_delta: null,
        ends_scenario: false,
        exit_type: "normal",
        is_exclusive_open: true,
      } as any;

    case NodeType.ASSIGN_ACTION:
      return {
        ...base,
        node_tag: "assign_action",
        target: {
          node_path: null,
          name: "",
          constant: null,
          vtype: VariableReferenceType.LVAL,
        },
        source: {
          node_path: null,
          name: "",
          constant: null,
          vtype: VariableReferenceType.RVAL,
        },
        value_template: null,
        transform: TransformKind.IDENTITY,
        transform_expr: null,
      } as any;

    case NodeType.APPEND_ACTION:
      return {
        ...base,
        node_tag: "append_action",
        target: {
          node_path: null,
          name: "",
          constant: null,
          vtype: VariableReferenceType.LVAL,
        },
        source: {
          node_path: null,
          name: "",
          constant: null,
          vtype: VariableReferenceType.RVAL,
        },
        value_template: null,
        transform: TransformKind.IDENTITY,
        transform_expr: null,
      } as any;

    case NodeType.QUESTION:
      return {
        ...base,
        node_tag: "question",
        goal_question: null,
        question_id: null,
        is_exclusive_open: true,
      } as any;

    default:
      return {
        ...base,
        node_tag: "sequence",
      } as any;
  }
}

/**
 * Generate a unique node ID
 */
export function generateNodeId(type: NodeType): string {
  return `${type}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Validate control graph structure
 */
export function validateControlGraph(graph: ControlGraph): string[] {
  const errors: string[] = [];

  if (!graph.root) {
    errors.push("Control graph must have a root node");
    return errors;
  }

  if (!graph.nodes[graph.root]) {
    errors.push(`Root node '${graph.root}' does not exist`);
  }

  // Check for orphaned nodes (except root)
  const reachableNodes = new Set<string>();
  const queue = [graph.root];

  while (queue.length > 0) {
    const nodeId = queue.shift()!;
    if (reachableNodes.has(nodeId)) continue;
    reachableNodes.add(nodeId);

    const node = graph.nodes[nodeId];
    if (node) {
      queue.push(...node.children);
    }
  }

  Object.keys(graph.nodes).forEach((nodeId) => {
    if (!reachableNodes.has(nodeId)) {
      errors.push(`Node '${nodeId}' is not reachable from root`);
    }
  });

  // Check for circular references
  const visited = new Set<string>();
  const inPath = new Set<string>();

  function detectCycle(nodeId: string): boolean {
    if (inPath.has(nodeId)) {
      errors.push(`Circular reference detected at node '${nodeId}'`);
      return true;
    }
    if (visited.has(nodeId)) return false;

    visited.add(nodeId);
    inPath.add(nodeId);

    const node = graph.nodes[nodeId];
    if (node) {
      for (const childId of node.children) {
        if (detectCycle(childId)) return true;
      }
    }

    inPath.delete(nodeId);
    return false;
  }

  detectCycle(graph.root);

  return errors;
}

/**
 * Get clean, user-friendly label for a node (no IDs)
 */
export function getNodeDisplayLabel(node: ControlNode): string {
  const metadata = NODE_METADATA[node.node_tag as NodeType];
  const baseLabel = metadata?.label || node.node_tag;

  switch (node.node_tag) {
    case "declare":
      return "Variables";

    case "map":
      const mapNode = node as any;
      const iterVar = mapNode.iter_var || "item";
      const sourceVar = mapNode.source_set_var || "collection";
      return `For each ${iterVar} in ${sourceVar}`;

    case "if":
      const ifNode = node as any;
      return `If ${ifNode.expr || "condition"}`;

    case "question":
      // Extract clean label from goal_question if available
      const questionNode = node as any;
      if (questionNode.goal_question?.topic) {
        // Try to create a short label from topic
        const topic = questionNode.goal_question.topic;
        const shortTopic = topic.length > 30 ? topic.substring(0, 30) + "..." : topic;
        return `Q: ${shortTopic}`;
      }
      if (questionNode.question_id) {
        return `Q: ${questionNode.question_id}`;
      }
      return "Question";

    case "share_info":
      return "Say to user";

    case "influence":
      return "Set mood";

    case "analyze":
      return "Analyze";

    case "assign_action":
      return "Set variable";

    case "append_action":
      return "Add to set";

    case "sequence":
      return "Sequence";

    case "group":
      return "Group";

    default:
      return baseLabel;
  }
}

/**
 * Get preview text for node (shown below title)
 */
export function getNodePreview(node: ControlNode): string {
  switch (node.node_tag) {
    case "declare":
      const declareNode = node as any;
      const varNames = declareNode.variables?.map((v: any) => v.name) || [];
      if (varNames.length === 0) return "No variables";
      if (varNames.length <= 2) return varNames.join(", ");
      return `${varNames[0]}, ${varNames[1]}, +${varNames.length - 2} more`;

    case "map":
      const mapNode = node as any;
      return `Loops through ${mapNode.source_set_var || "collection"}`;

    case "if":
      const ifNode = node as any;
      const expr = ifNode.expr || "";
      return expr.length > 40 ? expr.substring(0, 40) + "..." : expr;

    case "question":
      const questionNode = node as any;
      if (questionNode.goal_question?.initial_question) {
        const q = questionNode.goal_question.initial_question;
        return q.length > 50 ? q.substring(0, 50) + "..." : q;
      }
      if (questionNode.question_id) {
        return `Ref: ${questionNode.question_id}`;
      }
      return "";

    case "share_info":
      const shareNode = node as any;
      const template = shareNode.template || "";
      return template.length > 50 ? template.substring(0, 50) + "..." : template;

    case "influence":
      const influenceNode = node as any;
      if (influenceNode.guidance) {
        const guidance = influenceNode.guidance;
        return guidance.length > 50 ? guidance.substring(0, 50) + "..." : guidance;
      }
      const deltas = [];
      if (influenceNode.valence_delta !== null) deltas.push(`mood ${influenceNode.valence_delta > 0 ? '+' : ''}${influenceNode.valence_delta}`);
      if (influenceNode.activation_delta !== null) deltas.push(`energy ${influenceNode.activation_delta > 0 ? '+' : ''}${influenceNode.activation_delta}`);
      return deltas.join(", ") || "Adjusts bot state";

    case "analyze":
      const analyzeNode = node as any;
      const question = analyzeNode.criteria?.analyze_question || "";
      return question.length > 50 ? question.substring(0, 50) + "..." : question;

    case "assign_action":
      const assignNode = node as any;
      const targetName = assignNode.target?.name || "target";
      const sourceName = assignNode.source?.name || assignNode.source?.constant || "value";
      return `${targetName} = ${sourceName}`;

    case "append_action":
      const appendNode = node as any;
      const tgtName = appendNode.target?.name || "set";
      const srcName = appendNode.source?.name || appendNode.source?.constant || "item";
      return `${tgtName}.add(${srcName})`;

    case "sequence":
      return "Runs children in order";

    case "group":
      return "Runs children in parallel";

    default:
      return "";
  }
}

/**
 * Get human-readable description of what this node does in the conversation
 */
export function getNodeHumanDescription(node: ControlNode): string {
  switch (node.node_tag) {
    case "declare":
      return "Sets up variables that can be used by child nodes";

    case "sequence":
      return "Executes children nodes one after another in order";

    case "map":
      const mapNode = node as any;
      return `Repeats children for each item in ${mapNode.source_set_var || "a collection"}`;

    case "group":
      return "Groups nodes together - all children can execute in parallel";

    case "if":
      return "Only executes children if the condition is true";

    case "analyze":
      return "Analyzes the conversation to extract information based on criteria";

    case "share_info":
      return "Bot shares information by speaking the templated message";

    case "influence":
      const influenceNode = node as any;
      if (influenceNode.ends_scenario) {
        return "Influences bot behavior and emotions, then ends the conversation";
      }
      return "Influences bot's emotional state and behavior guidance";

    case "assign_action":
      return "Sets a variable to a specific value";

    case "append_action":
      return "Adds an item to a collection (set)";

    case "question":
      const questionNode = node as any;
      if (questionNode.question_id) {
        return "Bot asks a pre-defined interview question";
      }
      return "Bot asks an interview question and analyzes the response";

    default:
      return "";
  }
}

/**
 * Extract all declared variables from the graph
 */
export function getAllVariables(nodes: Node[]): string[] {
  const variables: Set<string> = new Set();

  nodes.forEach((node) => {
    const nodeData = node.data as ControlNode;
    if (nodeData.node_tag === "declare") {
      const declareNode = nodeData as any;
      declareNode.variables?.forEach((v: any) => {
        variables.add(v.name);
      });
    }
  });

  return Array.from(variables).sort();
}

/**
 * Find nodes that use a specific variable
 */
export function findVariableUsages(nodes: Node[], variableName: string): string[] {
  const usageNodeIds: string[] = [];

  nodes.forEach((node) => {
    const nodeData = node.data as ControlNode;

    // Check assign/append actions
    if (nodeData.node_tag === "assign_action" || nodeData.node_tag === "append_action") {
      const actionNode = nodeData as any;
      if (actionNode.target?.name === variableName ||
          actionNode.source?.name === variableName) {
        usageNodeIds.push(node.id);
      }
    }

    // Check if conditions
    if (nodeData.node_tag === "if") {
      const ifNode = nodeData as any;
      if (ifNode.expr?.includes(variableName)) {
        usageNodeIds.push(node.id);
      }
    }

    // Check map nodes
    if (nodeData.node_tag === "map") {
      const mapNode = nodeData as any;
      if (mapNode.source_set_var === variableName || mapNode.iter_var === variableName) {
        usageNodeIds.push(node.id);
      }
    }
  });

  return usageNodeIds;
}

/**
 * Find the declare node that defines a variable
 */
export function findVariableDeclaration(nodes: Node[], variableName: string): string | null {
  for (const node of nodes) {
    const nodeData = node.data as ControlNode;
    if (nodeData.node_tag === "declare") {
      const declareNode = nodeData as any;
      const hasVariable = declareNode.variables?.some((v: any) => v.name === variableName);
      if (hasVariable) {
        return node.id;
      }
    }
  }
  return null;
}

/**
 * Apply hierarchical tree layout to nodes using dagre
 * Organizes nodes from parent to children in a tree structure
 */
export function applyTreeLayout(
  nodes: Node[],
  edges: Edge[],
  direction: "TB" | "LR" = "TB"
): Node[] {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));

  // Configure the graph layout - adjusted for smaller, compact nodes
  dagreGraph.setGraph({
    rankdir: direction, // TB = top-to-bottom, LR = left-to-right
    nodesep: 60, // Horizontal spacing between nodes (reduced)
    ranksep: 100, // Vertical spacing between ranks (reduced)
    marginx: 40,
    marginy: 40,
  });

  // Add nodes to dagre graph with smaller dimensions
  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, {
      width: 190, // Smaller node width (was 250)
      height: 80, // Smaller node height (was 100)
    });
  });

  // Add edges to dagre graph
  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  // Calculate layout
  dagre.layout(dagreGraph);

  // Apply calculated positions to nodes
  return nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    return {
      ...node,
      position: {
        // Center the node on its calculated position
        x: nodeWithPosition.x - 95, // Half of node width (190/2)
        y: nodeWithPosition.y - 40, // Half of node height (80/2)
      },
    };
  });
}
